import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const billingPath = () => { const ws = localStorage.getItem("evr_last_ws"); return ws ? `/app/w/${ws}/billing` : "/app"; };

export default function PaymentResult({ cancelled = false }) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled) return;
    const sid = new URLSearchParams(search).get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); setTimeout(() => navigate(billingPath()), 2500); return; }
        if (data.status === "expired") { setState("error"); return; }
      } catch { /* retry */ }
      if (!stop && ++tries < 15) setTimeout(poll, 2000); else if (!stop) setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [cancelled, search, navigate]);

  const view = {
    checking: [Loader2, "Confirming your payment...", "This usually takes a few seconds."],
    paid: [CheckCircle2, "Payment confirmed", "Your plan is active. Taking you to Plan & billing..."],
    cancelled: [XCircle, "Checkout cancelled", "No payment was taken. You can choose a plan any time."],
    timeout: [Loader2, "Still confirming", "Your payment may still be processing. Check Plan & billing in a minute."],
    error: [XCircle, "We couldn't confirm this payment", "No changes were made. Please try again from Plan & billing."],
  }[state];
  const [Icon, title, text] = view;
  return (
    <div data-testid="payment-result-page" className="grid min-h-screen place-items-center bg-paper px-6 text-center">
      <div className="max-w-md rounded-xl border bg-white p-8 shadow-sm">
        <Icon className={`mx-auto h-10 w-10 ${state === "paid" ? "text-emerald-600" : state === "checking" ? "animate-spin text-navy" : "text-gold-dark"}`} />
        <h1 className="mt-4 font-display text-2xl font-bold text-navy" data-testid={`payment-result-${state}`}>{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{text}</p>
        <Link to={billingPath()} data-testid="payment-result-billing-link" className="mt-6 inline-block text-sm font-medium text-navy underline">Go to Plan & billing</Link>
      </div>
    </div>
  );
}
